<!--
  Near Miss Report Form
  
  Step-by-step incident reporting with:
  - 5 steps: Employee & Job Details, Incident Basics, Equipment, Incident Assessment, Sign-off
  - Comprehensive incident documentation with employee and job information
  - Checkbox group for near miss type selection with conditional "other" specification
  - Multiple conditional validation fields based on user selections
  - Dynamic attachment requirements (3 attachments when property damage is "Yes")
  - Form validation for safety compliance and incident documentation
  - Signature requirements on final step
-->

<!-- Use the form layout component to handle the wizard UI -->
<app-form-layout
  [title]="formTitle"
  [form]="form"
  [icon]="formIcon"
  [disabled]="submitting"
  [onSubmit]="onSubmit.bind(this)"
  [steps]="steps"
  [currentStep]="currentStep()"
  [completedSteps]="completedSteps()"
  [canNavigateToStep]="canNavigateToStep.bind(this)"
  [isStepCompleted]="isStepCompleted.bind(this)"
  [onNextStep]="onNextStep.bind(this)"
  [onPreviousStep]="onPreviousStep.bind(this)"
  [getProgressPercentage]="getProgressPercentage.bind(this)"
>
  <div
    *ngIf="form && fieldMappings"
    formContent
    [formGroup]="form"
    class="form-content"
  >
    <!-- Employee & Job Details Step -->
    <div *ngIf="currentStep() === steps[0].id" class="step-content">
      <ion-list>
        <ion-item-divider>
          <ion-label>
            <h1>{{ steps[0].title }}</h1>
            <p>{{ steps[0].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="employeeJobDetailsFormGroup"
                [control]="employeeJobDetailsFormGroup.get('employeeFirstName')!"
                [label]="fieldMappings['employeeFirstName']"
                [requiredField]="requiredFields['employeeFirstName']"
                placeholder="Enter first name"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="employeeJobDetailsFormGroup"
                [control]="employeeJobDetailsFormGroup.get('employeeLastName')!"
                [label]="fieldMappings['employeeLastName']"
                [requiredField]="requiredFields['employeeLastName']"
                placeholder="Enter last name"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-date-input
                [templateFormGroup]="employeeJobDetailsFormGroup"
                [control]="employeeJobDetailsFormGroup.get('dateOfBirth')!"
                [label]="fieldMappings['dateOfBirth']"
                [requiredField]="requiredFields['dateOfBirth']"
                class="custom-datetime"
              >
              </app-date-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-date-input
                [templateFormGroup]="employeeJobDetailsFormGroup"
                [control]="employeeJobDetailsFormGroup.get('dateOfHire')!"
                [label]="fieldMappings['dateOfHire']"
                [requiredField]="requiredFields['dateOfHire']"
      
                class="custom-datetime"
              >
              </app-date-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-text-input
                [templateFormGroup]="employeeJobDetailsFormGroup"
                [control]="employeeJobDetailsFormGroup.get('craftAndPosition')!"
                [label]="fieldMappings['craftAndPosition']"
                [requiredField]="requiredFields['craftAndPosition']"
                placeholder="Enter craft and position"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>

        <!-- Job Information Section -->
        <ion-item-divider>
          <ion-label>
            <h2>{{ steps[0].sections?.[1]?.title }}</h2>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="employeeJobDetailsFormGroup"
                [control]="employeeJobDetailsFormGroup.get('jobName')!"
                [label]="fieldMappings['jobName']"
                [requiredField]="requiredFields['jobName']"
                placeholder="Enter job name"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="employeeJobDetailsFormGroup"
                [control]="employeeJobDetailsFormGroup.get('shiftWorked')!"
                [label]="fieldMappings['shiftWorked']"
                [requiredField]="requiredFields['shiftWorked']"
                placeholder="Enter shift worked"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col >
              <app-text-input
                [templateFormGroup]="employeeJobDetailsFormGroup"
                [control]="employeeJobDetailsFormGroup.get('hoursWorkedPerDay')!"
                [label]="fieldMappings['hoursWorkedPerDay']"
                [requiredField]="requiredFields['hoursWorkedPerDay']"
                [type]="'number'"
                placeholder="Enter hours worked per day"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="employeeJobDetailsFormGroup"
                [control]="employeeJobDetailsFormGroup.get('supervisorFirstName')!"
                [label]="fieldMappings['supervisorFirstName']"
                [requiredField]="requiredFields['supervisorFirstName']"
                placeholder="Enter supervisor first name"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="employeeJobDetailsFormGroup"
                [control]="employeeJobDetailsFormGroup.get('supervisorLastName')!"
                [label]="fieldMappings['supervisorLastName']"
                [requiredField]="requiredFields['supervisorLastName']"
                placeholder="Enter supervisor last name"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

        </ion-grid>
      </ion-list>
    </div>

    <!-- Incident Basics Step -->
    <div *ngIf="currentStep() === steps[1].id" class="step-content">
      <ion-list>
        <ion-item-divider>
          <ion-label>
            <h1>{{ steps[1].title }}</h1>
            <p>{{ steps[1].description }}</p>
          </ion-label>
        </ion-item-divider>


        <ion-grid>
          <ion-row>
            <ion-col>
              <!-- Type of Near Miss Checkboxes -->
              <app-checkbox-input
                [templateFormGroup]="typeOfNearMissCheckboxesFormGroup"
                [checkboxes]="typeOfNearMissCheckboxes"
                [label]="fieldMappings['typeOfNearMiss']"
                [requiredField]="true"
              >
              </app-checkbox-input>
            </ion-col>
          </ion-row>
          <ion-row *ngIf="typeOfNearMissCheckboxesFormGroup.get('otherNearMiss')?.value">
            <ion-col size="12">
              <app-text-input
                [templateFormGroup]="incidentBasicsFormGroup"
                [control]="incidentBasicsFormGroup.get('specifyOther')!"
                [label]="fieldMappings['specifyOther']"
                [requiredField]="true"
                placeholder="Please specify the type of near miss"
              >
              </app-text-input>
            </ion-col>
          </ion-row>


          <ion-row>
            <ion-col size="12" size-md="6">
              <app-date-input
                [templateFormGroup]="incidentBasicsFormGroup"
                [control]="incidentBasicsFormGroup.get('dateOfIncident')!"
                [label]="fieldMappings['dateOfIncident']"
                [requiredField]="requiredFields['dateOfIncident']"
           
                class="custom-datetime"
              >
              </app-date-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentBasicsFormGroup"
                [control]="incidentBasicsFormGroup.get('timeOfIncident')!"
                [label]="fieldMappings['timeOfIncident']"
                [type]="'time'"
                [requiredField]="requiredFields['timeOfIncident']"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-date-input
                [templateFormGroup]="incidentBasicsFormGroup"
                [control]="incidentBasicsFormGroup.get('dateReported')!"
                [label]="fieldMappings['dateReported']"
                [requiredField]="requiredFields['dateReported']"
         
                class="custom-datetime"
              >
              </app-date-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentBasicsFormGroup"
                [control]="incidentBasicsFormGroup.get('timeReported')!"
                [label]="fieldMappings['timeReported']"
                [type]="'time'"
                [requiredField]="requiredFields['timeReported']"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentBasicsFormGroup"
                [control]="incidentBasicsFormGroup.get('incidentReportedToFirstName')!"
                [label]="fieldMappings['incidentReportedToFirstName']"
                [requiredField]="requiredFields['incidentReportedToFirstName']"
                placeholder="Enter first name"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentBasicsFormGroup"
                [control]="incidentBasicsFormGroup.get('incidentReportedToLastName')!"
                [label]="fieldMappings['incidentReportedToLastName']"
                [requiredField]="requiredFields['incidentReportedToLastName']"
                placeholder="Enter last name"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentBasicsFormGroup"
                [control]="incidentBasicsFormGroup.get('weather')!"
                [label]="fieldMappings['weather']"
                [requiredField]="requiredFields['weather']"
                placeholder="Enter weather conditions"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentBasicsFormGroup"
                [control]="incidentBasicsFormGroup.get('incidentLocation')!"
                [label]="fieldMappings['incidentLocation']"
                [requiredField]="requiredFields['incidentLocation']"
                placeholder="Enter exact location"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
                    <app-textarea-input
          [templateFormGroup]="incidentBasicsFormGroup"
          [control]="incidentBasicsFormGroup.get('incidentDescription')!"
          [label]="fieldMappings['incidentDescription']"
          [requiredField]="requiredFields['incidentDescription']"
          [rows]="4"
          placeholder="Describe the incident in detail"
        >
        </app-textarea-input>
        </ion-grid>

        <!-- Witnesses Section -->
        <ion-item-divider>
          <ion-label>
            <h2>{{ steps[1].sections?.[1]?.title }}</h2>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentBasicsFormGroup"
                [control]="incidentBasicsFormGroup.get('witness1')!"
                [label]="fieldMappings['witness1']"
                [requiredField]="requiredFields['witness1']"
                placeholder="Enter witness name/contact"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentBasicsFormGroup"
                [control]="incidentBasicsFormGroup.get('witness2')!"
                [label]="fieldMappings['witness2']"
                placeholder="Enter additional witness (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Equipment Step -->
    <div *ngIf="currentStep() === steps[2].id" class="step-content">
      <ion-list>
        <ion-item-divider>
          <ion-label>
            <h1>{{ steps[2].title }}</h1>
            <p>{{ steps[2].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="equipmentFormGroup"
                [control]="equipmentFormGroup.get('equipmentTool1')!"
                [label]="fieldMappings['equipmentTool1']"
                [requiredField]="requiredFields['equipmentTool1']"
                placeholder="Enter primary equipment/tool involved"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="equipmentFormGroup"
                [control]="equipmentFormGroup.get('equipmentTool2')!"
                [label]="fieldMappings['equipmentTool2']"
                placeholder="Enter additional equipment/tool (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-text-input
                [templateFormGroup]="equipmentFormGroup"
                [control]="equipmentFormGroup.get('anyDefects')!"
                [label]="fieldMappings['anyDefects']"
                placeholder="Describe any defects"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="equipmentFormGroup"
                [control]="equipmentFormGroup.get('coveredInPreTask')!"
                [label]="fieldMappings['coveredInPreTask']"
                [requiredField]="requiredFields['coveredInPreTask']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="equipmentFormGroup"
                [control]="equipmentFormGroup.get('wasPpeRequired')!"
                [label]="fieldMappings['wasPpeRequired']"
                [requiredField]="requiredFields['wasPpeRequired']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="equipmentFormGroup"
                [control]="equipmentFormGroup.get('wasPpeAvailable')!"
                [label]="fieldMappings['wasPpeAvailable']"
                [requiredField]="requiredFields['wasPpeAvailable']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>


   
    <!-- Incident Assessment -->
    <div *ngIf="currentStep() === steps[3].id" class="step-content">
      <ion-list>
        <ion-item-divider>
          <ion-label>
            <h1>{{ steps[3].title }}</h1>
            <p>{{ steps[3].description }}</p>
          </ion-label>
        </ion-item-divider>

        <!-- Injury Section -->
        <ion-item-divider>
          <ion-label>
            <h2>{{ steps[3].sections?.[0]?.title }}</h2>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="incidentAssessmentFormGroup"
                [control]="incidentAssessmentFormGroup.get('othersInvolved')!"
                [label]="fieldMappings['othersInvolved']"
                [requiredField]="requiredFields['othersInvolved']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row *ngIf="incidentAssessmentFormGroup.get('othersInvolved')?.value === 'Yes'">
            <ion-col size="12" size-md="6">
              <app-textarea-input
                [templateFormGroup]="incidentAssessmentFormGroup"
                [control]="incidentAssessmentFormGroup.get('othersExplanation')!"
                [label]="fieldMappings['othersExplanation']"
                [requiredField]="true"
                [rows]="2"
                placeholder="Explain others involved"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="incidentAssessmentFormGroup"
                [control]="incidentAssessmentFormGroup.get('injured')!"
                [label]="fieldMappings['injured']"
                [requiredField]="requiredFields['injured']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row *ngIf="incidentAssessmentFormGroup.get('injured')?.value === 'No'">
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="incidentAssessmentFormGroup"
                [control]="incidentAssessmentFormGroup.get('couldHaveBeenInjured')!"
                [label]="fieldMappings['couldHaveBeenInjured']"
                [requiredField]="true"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="incidentAssessmentFormGroup"
                [control]="incidentAssessmentFormGroup.get('bloodExposure')!"
                [label]="fieldMappings['bloodExposure']"
                [requiredField]="requiredFields['bloodExposure']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="incidentAssessmentFormGroup"
                [control]="incidentAssessmentFormGroup.get('seekMedicalTreatment')!"
                [label]="fieldMappings['seekMedicalTreatment']"
                [requiredField]="requiredFields['seekMedicalTreatment']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>

          <!-- Contact Safety Director message (conditional) -->
          <ion-row *ngIf="incidentAssessmentFormGroup.get('seekMedicalTreatment')?.value === 'Yes'">
          
              <ion-card color="warning">
                <ion-card-content>
                  <ion-text color="dark">
                    <h5><ion-icon name="alert-circle"></ion-icon>&nbsp;Contact the Safety Director - <strong>206.255.3388</strong></h5>
                  </ion-text>
                </ion-card-content>
              </ion-card>
          </ion-row>

          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="incidentAssessmentFormGroup"
                [control]="incidentAssessmentFormGroup.get('previousExposure')!"
                [label]="fieldMappings['previousExposure']"
                [requiredField]="requiredFields['previousExposure']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="incidentAssessmentFormGroup"
                [control]="incidentAssessmentFormGroup.get('aggravationOfPreviousInjury')!"
                [label]="fieldMappings['aggravationOfPreviousInjury']"
                [requiredField]="requiredFields['aggravationOfPreviousInjury']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>

          <!-- Conditional field for previous injury description -->
          <ion-row *ngIf="incidentAssessmentFormGroup.get('aggravationOfPreviousInjury')?.value === 'Yes'">
            <ion-col size="12">
              <app-textarea-input
                [templateFormGroup]="incidentAssessmentFormGroup"
                [control]="incidentAssessmentFormGroup.get('describePreviousInjury')!"
                [label]="fieldMappings['describePreviousInjury']"
                [requiredField]="requiredFields['describePreviousInjury']"
                [rows]="2"
                placeholder="Describe the previous injury"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>
          
          <ion-row>
            <ion-col size="12">
              <app-textarea-input
                [templateFormGroup]="incidentAssessmentFormGroup"
                [control]="incidentAssessmentFormGroup.get('worstCaseScenario')!"
                [label]="fieldMappings['worstCaseScenario']"
                [requiredField]="requiredFields['worstCaseScenario']"
                [rows]="3"
                placeholder="Specify what you think could have been a worst case scenario"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>

        

          <ion-row>
            <ion-col size="12">
              <app-textarea-input
                [templateFormGroup]="incidentAssessmentFormGroup"
                [control]="incidentAssessmentFormGroup.get('preventionSuggestion')!"
                [label]="fieldMappings['preventionSuggestion']"
                [requiredField]="requiredFields['preventionSuggestion']"
                [rows]="4"
                placeholder="How could this incident have been avoided?"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>

          <!-- Damage Section -->
          <ion-item-divider>
            <ion-label>
              <h2>{{ steps[3].sections?.[1]?.title }}</h2>
            </ion-label>
          </ion-item-divider>
          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="incidentAssessmentFormGroup"
                [control]="incidentAssessmentFormGroup.get('hasPropertyDamage')!"
                [label]="fieldMappings['hasPropertyDamage']"
                [requiredField]="requiredFields['hasPropertyDamage']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>

          <!-- Conditional field for damage description -->
          <ion-row *ngIf="incidentAssessmentFormGroup.get('hasPropertyDamage')?.value === 'Yes'">
            <ion-col size="12">
              <app-textarea-input
                [templateFormGroup]="incidentAssessmentFormGroup"
                [control]="incidentAssessmentFormGroup.get('damageDescription')!"
                [label]="fieldMappings['damageDescription']"
                [requiredField]="true"
                [rows]="2"
                placeholder="Describe the property damage"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>
                    <!-- Uploads Section -->
                    <ion-item-divider>
                      <ion-label>
                        <h2>{{ steps[3].sections?.[2]?.title }}</h2>
                      </ion-label>
                    </ion-item-divider>
          <ion-row >
            <ion-col size="12">
              <app-attachment-upload-section [uploadInfo]="formAttachmentRequirements!.uploads![0]"></app-attachment-upload-section>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

        <!-- Sign Off -->
        <div *ngIf="currentStep() === steps[4].id" class="step-content">
          <ion-list>
            <ion-item-divider>
              <ion-label>
                <h1>{{ steps[4].title }}</h1>
              </ion-label>
            </ion-item-divider>
        
            <ion-card color="primary" class="information-card">
              <ion-card-header><ion-card-title><ion-icon name="finger-print-outline"></ion-icon>&nbsp;Declaration</ion-card-title></ion-card-header>
              <ion-card-content>
                <ion-text>
                  <h4>The facts as I have stated them are true to the best of my knowledge. 
                    <br><br>In signing this form, I permit the release of all information related to this injury to my employer and his/her authorized representative. 
                    <br>
                    <br>A copy of this permission constitutes as an original.</h4>
                </ion-text>
              </ion-card-content>
            </ion-card>
    
            <!-- Signature and Date -->
            <ion-grid>
            
              <ion-row *ngFor="let signature of formAttachmentRequirements?.signatures">
                <ion-col size="12">
                  <app-signature-section [signatureInfo]="signature"></app-signature-section>
                </ion-col>
              </ion-row>
              <ion-row>
                <ion-col size="12">
                  <app-date-input
                    [templateFormGroup]="signOffFormGroup"
                    [control]="signOffFormGroup.get('signatureDate')!"
                    [label]="fieldMappings['signatureDate']"
                    [requiredField]="requiredFields['signatureDate']"
                    [today]="true"
                  
                    
                  >
                  </app-date-input>
                </ion-col>
              </ion-row>
            </ion-grid>
          </ion-list>
        </div>
  </div>
</app-form-layout>